import React, { useState, useCallback, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/shared/ui/dialog';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Textarea } from '@/shared/ui/textarea';
import { Label } from '@/shared/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { Checkbox } from '@/shared/ui/checkbox';
import { Switch } from '@/shared/ui/switch';
import { Loader2 } from 'lucide-react';

/**
 * フォームフィールドの型定義
 */
export type FormFieldType = 'text' | 'textarea' | 'number' | 'select' | 'boolean' | 'switch';

export interface FormField {
  key: string;
  label: string;
  type: FormFieldType;
  required?: boolean;
  placeholder?: string;
  options?: { value: string; label: string }[];
  min?: number;
  max?: number;
  validate?: (value: any) => string | null;
}

export interface GenericFormDialogProps<T> {
  trigger: React.ReactNode;
  title: string;
  fields: FormField[];
  initialData?: Partial<T>;
  onSubmit: (data: Partial<T>) => Promise<any>;
  isLoading?: boolean;
  submitLabel?: string;
  cancelLabel?: string;
}

/**
 * 汎用フォームダイアログコンポーネント
 * あらゆるマスターデータの作成・編集フォームで再利用可能な汎用ダイアログを提供する
 * 
 * @param trigger - ダイアログを開くトリガー要素
 * @param title - ダイアログタイトル
 * @param fields - フォームフィールド定義
 * @param initialData - 初期データ
 * @param onSubmit - 送信時のコールバック関数
 * @param isLoading - ローディング状態
 * @param submitLabel - 送信ボタンラベル
 * @param cancelLabel - キャンセルボタンラベル
 * @returns 汎用フォームダイアログコンポーネント
 */
export const GenericFormDialog = React.memo(<T extends Record<string, any>>({
  trigger,
  title,
  fields,
  initialData = {},
  onSubmit,
  isLoading = false,
  submitLabel = '保存',
  cancelLabel = 'キャンセル',
}: GenericFormDialogProps<T>) => {
  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useState<Partial<T>>(initialData);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // フォームデータの初期化
  const resetForm = useCallback(() => {
    setFormData(initialData);
    setErrors({});
  }, [initialData]);

  // ダイアログが開かれたときの処理
  const handleOpenChange = useCallback((open: boolean) => {
    setIsOpen(open);
    if (open) {
      resetForm();
    }
  }, [resetForm]);

  // フィールド値の変更処理
  const handleFieldChange = useCallback((key: string, value: any) => {
    setFormData(prev => ({ ...prev, [key]: value }));

    // エラーをクリア
    if (errors[key]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[key];
        return newErrors;
      });
    }
  }, [errors]);

  // バリデーション処理
  const validateForm = useCallback((): boolean => {
    const newErrors: Record<string, string> = {};

    fields.forEach(field => {
      const value = formData[field.key];

      // 必須チェック
      if (field.required && (!value || (typeof value === 'string' && value.trim() === ''))) {
        newErrors[field.key] = `${field.label}は必須です`;
        return;
      }

      // カスタムバリデーション
      if (field.validate && value !== undefined && value !== null && value !== '') {
        const validationError = field.validate(value);
        if (validationError) {
          newErrors[field.key] = validationError;
        }
      }

      // 数値型の範囲チェック
      if (field.type === 'number' && value !== undefined && value !== null && value !== '') {
        const numValue = Number(value);
        if (isNaN(numValue)) {
          newErrors[field.key] = `${field.label}は数値で入力してください`;
        } else {
          if (field.min !== undefined && numValue < field.min) {
            newErrors[field.key] = `${field.label}は${field.min}以上で入力してください`;
          }
          if (field.max !== undefined && numValue > field.max) {
            newErrors[field.key] = `${field.label}は${field.max}以下で入力してください`;
          }
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [fields, formData]);

  // フォーム送信処理
  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      await onSubmit(formData);
      setIsOpen(false);
      resetForm();
    } catch (error) {
      // エラーはonSubmit側で処理される想定
      console.error('Form submission error:', error);
    }
  }, [formData, validateForm, onSubmit, resetForm]);

  // フィールドレンダリング関数
  const renderField = useCallback((field: FormField) => {
    const value = formData[field.key];
    const error = errors[field.key];

    const fieldId = `field-${field.key}`;

    switch (field.type) {
      case 'text':
        return (
          <div key={field.key}>
            <Label htmlFor={fieldId} className={field.required ? 'required' : ''}>
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <Input
              id={fieldId}
              value={value || ''}
              onChange={(e) => handleFieldChange(field.key, e.target.value)}
              placeholder={field.placeholder}
              className={error ? 'border-red-500' : ''}
            />
            {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
          </div>
        );

      case 'textarea':
        return (
          <div key={field.key}>
            <Label htmlFor={fieldId} className={field.required ? 'required' : ''}>
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <Textarea
              id={fieldId}
              value={value || ''}
              onChange={(e) => handleFieldChange(field.key, e.target.value)}
              placeholder={field.placeholder}
              className={error ? 'border-red-500' : ''}
            />
            {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
          </div>
        );

      case 'number':
        return (
          <div key={field.key}>
            <Label htmlFor={fieldId} className={field.required ? 'required' : ''}>
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <Input
              id={fieldId}
              type="number"
              value={value || ''}
              onChange={(e) => handleFieldChange(field.key, e.target.value ? Number(e.target.value) : '')}
              placeholder={field.placeholder}
              min={field.min}
              max={field.max}
              className={error ? 'border-red-500' : ''}
            />
            {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
          </div>
        );

      case 'select':
        return (
          <div key={field.key}>
            <Label className={field.required ? 'required' : ''}>
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <Select
              value={value || ''}
              onValueChange={(newValue) => handleFieldChange(field.key, newValue)}
            >
              <SelectTrigger className={error ? 'border-red-500' : ''}>
                <SelectValue placeholder={field.placeholder || `${field.label}を選択`} />
              </SelectTrigger>
              <SelectContent>
                {field.options?.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
          </div>
        );

      case 'boolean':
        return (
          <div key={field.key} className="flex items-center space-x-2">
            <Checkbox
              id={fieldId}
              checked={value || false}
              onCheckedChange={(checked) => handleFieldChange(field.key, checked)}
              className={error ? 'border-red-500' : ''}
            />
            <Label htmlFor={fieldId} className="text-sm font-normal">
              {field.label}
            </Label>
            {error && <p className="text-red-500 text-sm">{error}</p>}
          </div>
        );

      case 'switch':
        return (
          <div key={field.key} className="flex flex-row items-center justify-between rounded-lg border p-3 shadow-sm">
            <div className="space-y-0.5">
              <Label htmlFor={fieldId} className="text-sm font-medium">
                {field.label}
                {field.required && <span className="text-red-500 ml-1">*</span>}
              </Label>
              {field.placeholder && (
                <p className="text-sm text-muted-foreground">
                  {field.placeholder}
                </p>
              )}
            </div>
            <Switch
              id={fieldId}
              checked={value || false}
              onCheckedChange={(checked) => handleFieldChange(field.key, checked)}
            />
            {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
          </div>
        );

      default:
        return null;
    }
  }, [formData, errors, handleFieldChange]);

  // メモ化されたフィールドレンダリング
  const renderedFields = useMemo(() => {
    return fields.map(renderField);
  }, [fields, renderField]);

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        {trigger}
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {renderedFields}

          <div className="flex justify-end space-x-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsOpen(false)}
              disabled={isLoading}
            >
              {cancelLabel}
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  保存中...
                </>
              ) : (
                submitLabel
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
});

GenericFormDialog.displayName = 'GenericFormDialog';

export default GenericFormDialog;